import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
// INTENTIONAL raw axios import — AuthContext configures the global
// axios instance (default headers, interceptors, token lifecycle).
// All other files should use lib/api-client.js wrappers instead.
import axios from "axios";
import { API_BASE as API } from "./lib/api";
import { getAuthToken, setAuthToken, removeAuthToken } from "./lib/auth-token";
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAuthToken());
  const [loading, setLoading] = useState(true);
  const [onboardingDone, setOnboardingDone] = useState(null);
  const [activeRole, setActiveRole] = useState(localStorage.getItem("capymatch_active_role") || null);
  const refreshingRef = useRef(null);

  // Persist tokens
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setAuthToken(token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      removeAuthToken();
      localStorage.removeItem("capymatch_refresh_token");
    }
  }, [token]);

  // Send effective role header with every request
  useEffect(() => {
    const id = axios.interceptors.request.use((config) => {
      const role = localStorage.getItem("capymatch_active_role");
      if (role) config.headers["X-Effective-Role"] = role;
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  // Refresh token helper
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem("capymatch_refresh_token");
    if (!refreshToken) throw new Error("No refresh token");

    // Dedupe: if already refreshing, return the same promise
    if (refreshingRef.current) return refreshingRef.current;

    refreshingRef.current = axios
      .post(`${API}/auth/refresh`, { refresh_token: refreshToken })
      .then((res) => {
        const newToken = res.data.token;
        const newRefresh = res.data.refresh_token;
        setToken(newToken);
        localStorage.setItem("capymatch_refresh_token", newRefresh);
        setUser(res.data.user);
        return newToken;
      })
      .catch((err) => {
        // Refresh failed — force logout
        setToken(null);
        setUser(null);
        setOnboardingDone(null);
        localStorage.removeItem("capymatch_refresh_token");
        throw err;
      })
      .finally(() => {
        refreshingRef.current = null;
      });

    return refreshingRef.current;
  }, []);

  // Axios interceptor: auto-refresh on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const originalRequest = error.config;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest.url?.includes("/auth/login") &&
          !originalRequest.url?.includes("/auth/refresh") &&
          !originalRequest.url?.includes("/auth/register")
        ) {
          originalRequest._retry = true;
          try {
            const newToken = await refreshAccessToken();
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch {
            return Promise.reject(error);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshAccessToken]);

  // Check onboarding status
  const [directorOnboardingNeeded, setDirectorOnboardingNeeded] = useState(false);

  const checkOnboarding = async (userData) => {
    if (userData?.role === "athlete" || userData?.role === "parent") {
      try {
        const res = await axios.get(`${API}/athlete/onboarding-status`);
        setOnboardingDone(res.data.completed);
      } catch {
        setOnboardingDone(true);
      }
    } else if (userData?.role === "platform_admin" || userData?.role === "director") {
      // Check director onboarding
      try {
        const res = await axios.get(`${API}/onboarding/status`);
        const completed = res.data.onboarding_completed;
        setOnboardingDone(true); // Don't block app loading
        setDirectorOnboardingNeeded(!completed);
      } catch {
        setOnboardingDone(true);
        setDirectorOnboardingNeeded(false);
      }
    } else {
      setOnboardingDone(true);
    }
  };

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/auth/me`)
      .then(async (res) => {
        setUser(res.data);
        await checkOnboarding(res.data);
      })
      .catch(() => {
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setToken(res.data.token);
    if (res.data.refresh_token) {
      localStorage.setItem("capymatch_refresh_token", res.data.refresh_token);
    }
    const userData = res.data.user;
    setUser(userData);
    if (userData?.role === "athlete" || userData?.role === "parent") {
      try {
        const obRes = await axios.get(`${API}/athlete/onboarding-status`, {
          headers: { Authorization: `Bearer ${res.data.token}` },
        });
        setOnboardingDone(obRes.data.completed);
      } catch {
        setOnboardingDone(true);
      }
    } else {
      setOnboardingDone(true);
      // Check director onboarding for admin/director
      if (userData?.role === "platform_admin" || userData?.role === "director") {
        try {
          const obRes = await axios.get(`${API}/onboarding/status`, {
            headers: { Authorization: `Bearer ${res.data.token}` },
          });
          setDirectorOnboardingNeeded(!obRes.data.onboarding_completed);
        } catch { setDirectorOnboardingNeeded(false); }
      }
    }
    return userData;
  };

  const register = async (email, password, name, role) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name, role });
    setToken(res.data.token);
    if (res.data.refresh_token) {
      localStorage.setItem("capymatch_refresh_token", res.data.refresh_token);
    }
    const userData = res.data.user;
    setUser(userData);
    if (userData?.role === "athlete" || userData?.role === "parent") {
      try {
        const obRes = await axios.get(`${API}/athlete/onboarding-status`, {
          headers: { Authorization: `Bearer ${res.data.token}` },
        });
        setOnboardingDone(obRes.data.completed);
      } catch {
        setOnboardingDone(true);
      }
    } else {
      setOnboardingDone(true);
    }
    return userData;
  };

  const googleLogin = async (credential) => {
    const res = await axios.post(`${API}/auth/google`, { credential });
    setToken(res.data.token);
    if (res.data.refresh_token) {
      localStorage.setItem("capymatch_refresh_token", res.data.refresh_token);
    }
    const userData = res.data.user;
    setUser(userData);
    if (userData?.role === "athlete" || userData?.role === "parent") {
      try {
        const obRes = await axios.get(`${API}/athlete/onboarding-status`, {
          headers: { Authorization: `Bearer ${res.data.token}` },
        });
        setOnboardingDone(obRes.data.completed);
      } catch {
        setOnboardingDone(true);
      }
    } else {
      setOnboardingDone(true);
    }
    return userData;
  };

  const loginWithTokens = async (tokenData) => {
    setToken(tokenData.token);
    if (tokenData.refresh_token) {
      localStorage.setItem("capymatch_refresh_token", tokenData.refresh_token);
    }
    const userData = tokenData.user;
    setUser(userData);
    await checkOnboarding(userData);
    return userData;
  };

  const completeOnboarding = () => setOnboardingDone(true);

  const switchRole = async (role) => {
    setActiveRole(role);
    localStorage.setItem("capymatch_active_role", role);
    // Re-check director onboarding when switching to director-like roles
    if (role === "director" || role === "platform_admin") {
      try {
        const res = await axios.get(`${API}/onboarding/status`);
        setDirectorOnboardingNeeded(!res.data.onboarding_completed);
      } catch { /* keep current state */ }
    }
  };

  // Derive the effective role: activeRole override if user has multiple roles
  const effectiveRole = (user?.roles && user.roles.length > 1 && activeRole && user.roles.includes(activeRole))
    ? activeRole
    : (user?.role || null);

  // When user changes, initialize activeRole if not set or invalid
  useEffect(() => {
    if (user?.roles && user.roles.length > 1) {
      if (!activeRole || !user.roles.includes(activeRole)) {
        const defaultRole = user.roles[0];
        setActiveRole(defaultRole);
        localStorage.setItem("capymatch_active_role", defaultRole);
      }
    } else if (user?.role) {
      setActiveRole(user.role);
      localStorage.setItem("capymatch_active_role", user.role);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch { /* ignore */ }
    setToken(null);
    setUser(null);
    setOnboardingDone(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, register, googleLogin, loginWithTokens, logout, onboardingDone, completeOnboarding, directorOnboardingNeeded, setDirectorOnboardingNeeded, activeRole, effectiveRole, switchRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
